import { forwardRef } from 'react';

import { cn } from '../lib/cn.js';
import { DialogFrame, type DialogFrameProps } from './Modal.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DRAWER — THE RECORD DETAIL PANE. A MODAL DIALOG ANCHORED TO A SCREEN EDGE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * It is `Modal`'s `DialogFrame` with different geometry, and reusing that component rather
 * than wrapping `Dialog` a second time is the point: a drawer and a modal that each hand-roll
 * their own Radix scaffolding drift, and the half that drifts is never the padding — it is the
 * close button's name, or the focus restore, in whichever of the two is touched less often.
 * Read `Modal.tsx`'s header for the accessibility argument; all of it applies here unchanged.
 *
 * ── IT IS STILL A MODAL DIALOG, AND THAT IS NOT A DEFAULT NOBODY THOUGHT ABOUT ──
 * A drawer looks like a panel that sits beside the page, so the tempting implementation is a
 * non-modal one that leaves the table behind it live. That is a genuinely different widget: no
 * focus trap, so Tab walks out of the open pane into the rows underneath and the user has no
 * indication they have left; no `aria-hidden` on the rest, so a screen reader reads the pane
 * and the table as one continuous document. If a pane really should stay open while the list
 * is used, it belongs in the layout as a labelled `<section>`, not as a dialog with its
 * modality switched off — so there is no `modal` prop to switch.
 *
 * ── `side` IS PHYSICAL, AND THAT IS A NOTED LIMITATION ─────────────────────
 * `right`/`left`, not `end`/`start`. This console is English-only today and nothing wires
 * Radix's `Direction` provider, so logical properties would be ceremony with no reader. When
 * an RTL locale arrives the change is `right-0`/`left-0` → `end-0`/`start-0` in `SIDES` and
 * the prop names alongside it — recorded here so it is a rename and not a rediscovery.
 *
 * Right is the default because that is where this console opens detail panes: the list keeps
 * its left-hand alignment, so the row a user was reading does not move under the pointer.
 */

export type DrawerSide = 'right' | 'left';
export type DrawerSize = 'sm' | 'md' | 'lg';

/**
 * `inset-y-0` + `h-full` rather than a height: the panel is the full height of the viewport by
 * construction, and `DialogFrame`'s column layout puts the scroll on the body region so the
 * header and footer stay put while the record scrolls.
 *
 * `w-[calc(100%-2rem)]` is the FLOOR-CLEARING part, not the width — see `MODAL_FRAME`. It caps
 * the drawer below the viewport on a narrow screen so a 320px phone still shows a strip of the
 * scrim; a `w-96` here would silently become a full-bleed panel with no way back except the
 * close button.
 */
const DRAWER_FRAME = 'inset-y-0 h-full w-[calc(100%-2rem)]';

/** Rounded on the inner edge only — the outer one is flush against the viewport. */
const SIDES: Record<DrawerSide, string> = {
  right: 'right-0 rounded-l-lg',
  left: 'left-0 rounded-r-lg',
};

const SIZES: Record<DrawerSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

export interface DrawerProps extends DialogFrameProps {
  side?: DrawerSide;
  size?: DrawerSize;
}

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  { side = 'right', size = 'md', className, ...rest },
  ref,
) {
  return (
    <DialogFrame
      ref={ref}
      className={cn(DRAWER_FRAME, SIDES[side], SIZES[size], className)}
      {...rest}
    />
  );
});
